/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import router from '@modules/routing/router'

import type { Location } from 'react-router-dom'

export type MinimalLocation = Partial<Location>

function stringOrLocationToUrl(to: string | MinimalLocation): string {
  if (typeof to === 'string') {
    return to
  }

  let url = to.pathname ?? ''

  if (to.search) {
    url += '?' + to.search
  }

  if (to.hash) {
    url += '#' + to.hash
  }

  return url
}

/**
 * Compatibility layer for npm `history` package still in use in older components. Migrate from this object to react router hooks as practical.
 *
 * @deprecated
 */
const history = {
  push: (url: string | MinimalLocation, state: unknown = {}) => {
    const stateToUse = (typeof url === 'object' && url.state) || state

    if (!router.current) {
      // fallback option for very early in the app lifecycle when the router may not have been initialized
      window.history.pushState(state, '', stringOrLocationToUrl(url))
      return
    }

    router.current.navigate(url, { state: stateToUse })
  },
  replace: (url: string | MinimalLocation) => {
    if (!router.current) {
      // fallback option for very early in the app lifecycle when the router may not have been initialized
      window.location.replace(stringOrLocationToUrl(url))
      return
    }

    router.current.navigate(url, { replace: true })
  },
  goBack: () => {
    if (!router.current) {
      window.history.go(-1)
      return
    }

    router.current.navigate(-1)
  },
}

export default history
